import {
  Box,
  Checkbox,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import Pagination from "components/Pagination/Pagination";
import { shallow } from "zustand/shallow";
import { t } from "i18next";
import React, { useCallback, useMemo, useRef, memo, useEffect } from "react";

import { STATUS_WORKING } from "constants";
import { useEmployeeStore } from "store/employeeStore";
import { DISABLED_EMPLOYEE_STATUS } from "constants";
import { filterOnlyEnableEmployee } from "utils";
import { isAllEmployeeChecked } from "utils";
import { toast } from "react-toastify";

const SearchEmployeeResult = (props) => {
  const {
    maxHeigh,
    employeeIdsCheckedState,
    setEmployeeIdsCheckedState,
    onNextBulkAssign,
    onBackBulkAssign,
    isBulkAssign = false,
    maxEmployee = null,
  } = props;
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const indexStart = useRef(0);

  const {
    setParamsListEmployee,
    setPagination,
    setSearchedEmployees,
  } = useEmployeeStore(
    (s) => ({
      setParamsListEmployee: s.setParamsListEmployee,
      setPagination: s.setPagination,
      setSearchedEmployees: s.setSearchedEmployees,
    }),
    shallow
  );

  const {
    paramsListEmployee,
    pagination,
    isSelectedAllEmployee,
    isShowSearchData,
    selectedEmployeeIds,
    allEmployees,
    searchedEmployees,
    isLoading,
    totalSearchedEmployees: totalCount,
  } = useEmployeeStore(
    (s) => ({
      isLoading: s.isLoading,
      paramsListEmployee: s.paramsListEmployee,
      pagination: s.pagination,
      isSelectedAllEmployee: s.isSelectedAllEmployee,
      isShowSearchData: s.isShowSearchData,
      selectedEmployeeIds: s.selectedEmployeeIds,
      allEmployees: s.allEmployees,
      searchedEmployees: s.searchedEmployees,
      totalSearchedEmployees: s.totalSearchedEmployees,
    }),
    shallow
  );

  useEffect(() => {
    if (selectedEmployeeIds.length && !searchedEmployees.length) {
      const selectedIds = new Set(selectedEmployeeIds);
      setSearchedEmployees(allEmployees.filter((e) => selectedIds.has(e.id)));
    }
  }, [isShowSearchData, allEmployees, selectedEmployeeIds, setSearchedEmployees]);

  // Only apply max employee limit when maxEmployee is explicitly provided (not null/undefined)
  const hasMaxEmployeeLimit = maxEmployee !== null && maxEmployee !== undefined;
  const effectiveMaxEmployee = hasMaxEmployeeLimit ? maxEmployee : null;

  const getMaxEmployeeErrorMessage = () =>
    t("pages.export.error_max_employees", { max: effectiveMaxEmployee });

  useEffect(() => {
    if (
      hasMaxEmployeeLimit &&
      effectiveMaxEmployee &&
      selectedEmployeeIds.length > effectiveMaxEmployee
    ) {
      setEmployeeIdsCheckedState(
        selectedEmployeeIds.slice(0, effectiveMaxEmployee)
      );
      toast.error(getMaxEmployeeErrorMessage());
    }
  }, [selectedEmployeeIds, hasMaxEmployeeLimit, effectiveMaxEmployee]);

  const getListPaginate = useMemo(() => {
    if (!paramsListEmployee.isPaging && searchedEmployees) {
      const offset = (pagination.page - 1) * pagination.limit;
      indexStart.current = offset;
      return searchedEmployees.slice(offset, pagination.limit + offset);
    }
    return searchedEmployees;
  }, [
    paramsListEmployee.isPaging,
    searchedEmployees,
    pagination.page,
    pagination.limit,
  ]);

  const pagingParams = useMemo(
    () => ({
      showCondition: paramsListEmployee.isPaging
        ? Math.ceil(totalCount / paramsListEmployee.limit) > 1
        : Math.ceil(searchedEmployees?.length / pagination.limit) > 1,
      total: paramsListEmployee.isPaging
        ? totalCount
        : searchedEmployees?.length,
      limit: paramsListEmployee.isPaging
        ? paramsListEmployee.limit
        : pagination.limit,
      page: paramsListEmployee.isPaging
        ? paramsListEmployee.page
        : pagination.page,
    }),
    [paramsListEmployee, totalCount, searchedEmployees, pagination]
  );

  const isCheckAll = useMemo(() => {
    const enabledEmployees = filterOnlyEnableEmployee(searchedEmployees);

    if (!enabledEmployees.length) return false;

    return isAllEmployeeChecked(enabledEmployees, employeeIdsCheckedState);
  }, [searchedEmployees, employeeIdsCheckedState]);

  const handleCheckboxChange = useCallback(
    (isChecked, id) => {
      if (isChecked) {
        if (!employeeIdsCheckedState.includes(id)) {
          setEmployeeIdsCheckedState([...employeeIdsCheckedState, id]);
        }

        return;
      }
      if (employeeIdsCheckedState.includes(id)) {
        setEmployeeIdsCheckedState(
          employeeIdsCheckedState.filter((x) => x !== id)
        );
      }
    },
    [employeeIdsCheckedState]
  );

  const onChangeSelectAll = useCallback(
    (isChecked, changeGroup = false) => {
      const currentSearchedList = [...searchedEmployees];
      const currentCheckedIds = [...employeeIdsCheckedState];

      if (isChecked) {
        let listFilter = [];

        if (changeGroup) {
          listFilter = [STATUS_WORKING.WORK.name];
        } else {
          listFilter = [STATUS_WORKING.WORK.name, STATUS_WORKING.LEAVE.name];
        }

        const prevCheckedIds = new Set(currentCheckedIds);
        const newSelections = currentSearchedList
          .filter(
            (employeeItem) =>
              !prevCheckedIds.has(employeeItem.id) &&
              listFilter.includes(employeeItem.work_status)
          )
          .map((item) => item.id);

        setEmployeeIdsCheckedState([...prevCheckedIds, ...newSelections]);
        return;
      }

      const newSelections = currentCheckedIds.filter(
        (id) => !currentSearchedList.some((item) => item.id === id)
      );

      setEmployeeIdsCheckedState([...newSelections]);
    },
    [
      paramsListEmployee.isPaging,
      isSelectedAllEmployee,
      searchedEmployees,
      setEmployeeIdsCheckedState,
      searchedEmployees,
    ]
  );

  const handlePageChange = useCallback(
    (nextPage) => {
      if (paramsListEmployee.isPaging) {
        setParamsListEmployee({
          ...paramsListEmployee,
          page: nextPage,
        });
      } else {
        setPagination({ page: nextPage });
      }
    },
    [paramsListEmployee, setParamsListEmployee, setPagination]
  );

  return (
    <>
      <TableContainerWrapper isBulkAssign={isBulkAssign}>
        <Table variant="simple" color={textColor} marginTop={"10px"} size="xsm">
          <Thead position="sticky" top={0} bg="white" zIndex={1}>
            <Tr my=".8rem" pl="0px">
              <Th borderColor={borderColor} width={"1%"} />
              <Th borderColor={borderColor} width={"20%"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.member_name")}
                </Text>
              </Th>
              <Th borderColor={borderColor} width={"10%"}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.hiring_date")}
                </Text>
              </Th>
              <Th borderColor={borderColor} width={"10%"}>
                <Text fontSize={"14px"}>{t("pages.course_form.position")}</Text>
              </Th>
              <Th borderColor={borderColor}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.job_title")}
                </Text>
              </Th>
              <Th borderColor={borderColor}>
                <Text fontSize={"14px"}>
                  {t("pages.course_form.affiliation")}
                </Text>
              </Th>
              <Th borderColor={borderColor} alignItems={"right"} width={"17%"}>
                <Box display={"flex"} justifyContent="end" pr="5px">
                  <Text fontSize={"14px"} pr={"5px"}>
                    {t("pages.course_form.select_all")}
                  </Text>
                  <Checkbox
                    colorScheme="green"
                    isChecked={isCheckAll}
                    disabled={isLoading}
                    onChange={(e) => {
                      onChangeSelectAll(e.target.checked);
                    }}
                  />
                </Box>
              </Th>
            </Tr>
          </Thead>
        </Table>

        {isLoading ? (
          <Box textAlign="center" mt="40px">
            <Spinner></Spinner>
          </Box>
        ) : (
          <Box overflowY="auto" maxHeight={isBulkAssign ? "100%" : maxHeigh}>
            <Table
              variant="simple"
              color={textColor}
              marginTop={"4px"}
              size="xsm"
            >
              <Tbody>
                {getListPaginate?.length ? (
                  getListPaginate.map((item, index) => (
                    <EmployeeRow
                      key={indexStart.current + index}
                      index={indexStart.current + index}
                      item={item}
                      onToggle={handleCheckboxChange}
                      employeeIdsCheckedState={employeeIdsCheckedState}
                    />
                  ))
                ) : (
                  <div style={{ textAlign: "center" }}>
                    <p>{t("messages.common_s003")}</p>
                  </div>
                )}
              </Tbody>
            </Table>
          </Box>
        )}
      </TableContainerWrapper>

      {!isLoading && !isBulkAssign && (
        <Box m="4px 0">
          {pagingParams.showCondition && (
            <Pagination
              total={pagingParams.total}
              perPage={pagingParams.limit}
              currentPage={pagingParams.page}
              onPageChange={handlePageChange}
            />
          )}
        </Box>
      )}
    </>
  );
};

const TableContainerWrapper = ({ children, isBulkAssign = false }) => {
  return isBulkAssign ? (
    <Box flex="1" overflow="hidden" display="flex" flexDirection="column">
      {children}
    </Box>
  ) : (
    <Box>{children}</Box>
  );
};

const EmployeeRow = memo(function EmployeeRow({
  index,
  item,
  onToggle,
  employeeIdsCheckedState,
}) {
  const isLoading = useEmployeeStore((s) => s.isLoading);
  const isChecked = employeeIdsCheckedState.includes(item.id);
  const disabled = DISABLED_EMPLOYEE_STATUS.includes(item.work_status);

  return (
    <Tr>
      <Td width={"1%"}>
        <Text fontSize={"14px"} pb=".5rem">
          {index + 1}
        </Text>
      </Td>
      <Td width={"20%"}>
        <Text fontSize={"14px"} pb=".5rem">
          {item.name}
        </Text>
      </Td>
      <Td width={"10%"}>
        <Text fontSize={"14px"} pb=".5rem">
          {item.join_date}
        </Text>
      </Td>
      <Td width={"10%"}>
        <Text fontSize={"14px"} pb=".5rem">
          {item.work_status}
        </Text>
      </Td>
      <Td>
        <Text fontSize={"14px"} pb=".5rem">
          {item.job}
        </Text>
      </Td>
      <Td>
        <Text fontSize={"14px"} pb=".5rem">
          {item.department_5_fullwidth ?? ""}
        </Text>
      </Td>
      <Td width={"17%"} textAlign={"right"} pr="5px">
        <Text fontSize={"14px"} pb=".5rem"></Text>
        <Checkbox
          disabled={disabled || isLoading}
          colorScheme="green"
          isChecked={!disabled && isChecked}
          onChange={(e) => onToggle(e.target.checked, item.id)}
        />
      </Td>
    </Tr>
  );
});

export default SearchEmployeeResult;
